import api from './api';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

export const userProfileService = {
  /**
   * Update current user's profile
   * @param {Object} data - Profile data (email, fullname, company, department)
   * @returns {Promise<Object>} Updated user data
   */
  async updateMyProfile(data) {
    try {
      const response = await api.patch(API_ENDPOINTS.AUTH.UPDATE_ME, data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      return { success: false, error: message };
    }
  },

  /**
   * Change current user's password
   * @param {string} currentPassword - Current password for verification
   * @param {string} newPassword - New password to set
   * @returns {Promise<Object>} Result of password change
   */
  async changeMyPassword(currentPassword, newPassword) {
    try {
      const response = await api.patch(API_ENDPOINTS.AUTH.UPDATE_ME, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      return { success: false, error: message };
    }
  },

  /**
   * Update both profile and password in a single request
   * @param {Object} profileData - Profile data
   * @param {string} currentPassword - Current password (if changing password)
   * @param {string} newPassword - New password (if changing password)
   * @returns {Promise<Object>} Updated user data
   */
  async updateMyProfileAndPassword(profileData, currentPassword, newPassword) {
    try {
      const data = { ...profileData };

      // Only include password fields if both are provided
      if (currentPassword && newPassword) {
        data.current_password = currentPassword;
        data.new_password = newPassword;
      }

      const response = await api.patch(API_ENDPOINTS.AUTH.UPDATE_ME, data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      return { success: false, error: message };
    }
  },

  /**
   * Admin: Update any user's profile
   * @param {string} username - Username of the user to update
   * @param {Object} data - User data to update
   * @returns {Promise<Object>} Updated user data
   */
  async updateUserByAdmin(username, data) {
    try {
      const response = await api.patch(API_ENDPOINTS.AUTH.UPDATE_USER(username), data);
      return { success: true, data: response.data };
    } catch (error) {
      let message = 'Failed to update user';

      if (error.response?.status === 403) {
        message = 'Admin privileges required';
      } else if (error.response?.status === 404) {
        message = 'User not found';
      } else if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }

      return { success: false, error: message };
    }
  },

  /**
   * Admin: Reset user's password
   * @param {string} username - Username of the user
   * @param {string} newPassword - New password to set
   * @returns {Promise<Object>} Result of password reset
   */
  async resetUserPassword(username, newPassword) {
    try {
      const response = await api.patch(API_ENDPOINTS.AUTH.UPDATE_USER(username), {
        new_password: newPassword
      });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      return { success: false, error: message };
    }
  },

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} True if valid
   */
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate password strength
   * @param {string} password - Password to validate
   * @returns {Object} Validation result with message
   */
  validatePassword(password) {
    if (!password) {
      return { valid: false, message: 'Password is required' };
    }

    if (password.length < 6) {
      return { valid: false, message: 'Password must be at least 6 characters' };
    }

    return { valid: true };
  }
};